<template>
  <a-select
    :value="value"
    :options="options"
    :loading="store.loading"
    :disabled="disabled"
    show-search
    allow-clear
    option-filter-prop="searchText"
    placeholder="搜索商品名称、类型或 ID"
    class="product-select"
    @update:value="emit('update:value', $event)"
    @dropdown-visible-change="(open: boolean) => open && store.load()"
    @keydown.esc.stop
  >
    <template #option="option">
      <div class="product-option" :title="option.label">
        <span>{{ option.label }}</span>
        <small>{{ option.detail }}</small>
      </div>
    </template>
    <template #notFoundContent>
      <span>{{ store.loading ? '正在加载商品…' : '没有匹配的可兑换商品' }}</span>
    </template>
  </a-select>
  <a-alert v-if="store.error" type="error" :message="store.error" class="select-note">
    <template #action><a-button size="small" @click="store.load">重试</a-button></template>
  </a-alert>
  <a-alert v-else-if="invalid" type="warning" :message="invalid" class="select-note" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useCommerceProductsStore } from '@/stores/commerce-products.store';
import {
  canRedeemProduct,
  productKindOptions,
  productPrice,
  productGrantSummary,
  productValidity,
} from '@/utils/commerce-products';

const props = defineProps<{ value?: string; disabled?: boolean }>();
const emit = defineEmits<{ 'update:value': [value: string | undefined] }>();
const store = useCommerceProductsStore();
const selected = computed(() => store.products.find((item) => item.id === props.value));
const invalid = computed(() => {
  if (!props.value || store.loading || store.error) return '';
  if (!selected.value) return '原商品已不可用，请重新选择。';
  if (!canRedeemProduct(selected.value)) return '原商品已下架或属于自动续费，请重新选择。';
  return '';
});
type ProductOption = {
  value: string;
  label: string;
  searchText: string;
  detail: string;
  disabled?: boolean;
};
const options = computed(() => {
  const groups: Array<{ label: string; options: ProductOption[] }> = productKindOptions
    .map((kind) => ({
      label: kind.label,
      options: store.sortedProducts
        .filter((item) => item.kind === kind.value && canRedeemProduct(item))
        .map((item) => ({
          value: item.id,
          label: item.name,
          searchText: `${item.name} ${item.id} ${kind.label}`,
          detail: `${productPrice(item)} · ${productGrantSummary(item)} · ${productValidity(item)}`,
        })),
    }))
    .filter((group) => group.options.length);
  if (props.value && (!selected.value || !canRedeemProduct(selected.value))) {
    groups.push({
      label: '原选择（不可兑换）',
      options: [
        {
          value: props.value,
          label: selected.value?.name ?? `已不可用商品 · ${props.value}`,
          searchText: props.value,
          detail: '请重新选择可兑换商品',
          disabled: true,
        },
      ],
    });
  }
  return groups;
});
</script>

<style scoped>
.product-select {
  width: 100%;
}
.product-option {
  display: flex;
  flex-direction: column;
  white-space: normal;
  overflow-wrap: anywhere;
}
.product-option small {
  color: var(--aw-text-secondary);
}
.select-note {
  margin-top: 8px;
}
</style>
